/**
 * Auction seed data - one auction row per auction listing plus anonymized bid
 * history (AUC-08). currentBid / endsAt / reserveMet mirror listing.seed.ts.
 * Auction 1001 keeps BIN available per the mockup, overriding strict AUC-04.
 * Auctions 1017/1018 are settled showcases (won/lost for the mock shopper)
 * and 1019 is the Scheduled visibility showcase (AUC-01). Rows 2001+ are
 * derived from the deterministic listing generator so its auction listings
 * stay consistent (bids, increments, reserves) with the catalog.
 */
import { AuctionResultState } from '@core/models/auction-result.state';
import { AuctionOutcome, AuctionStatus, BidStatus } from '@core/models/auction.enum';
import { AuctionState, BidState } from '@core/models/auction.state';
import { resolveBidIncrement } from '@core/models/platform-config.state';

import { generatedListingSpecs } from '../listing/listing.seed';
import { seedPlatformConfig } from '../platform-config/platform-config.seed';
import { daysAgo, daysFromNow, hoursAgo, hoursFromNow, minutesAgo, minutesFromNow } from '../seed-time';

/** AUC-02 increments come from the platform band table, never per auction. */
const incrementFor = (currentPrice: number): number =>
  resolveBidIncrement(seedPlatformConfig().bidIncrements, currentPrice);

interface AuctionSeed {
  id: number;
  listingId: number;
  startsAt: Date;
  endsAt: Date;
  startPrice: number;
  currentBid: number;
  bidCount: number;
  bidderCount: number;
  reservePrice?: number;
  reserveMet?: boolean;
  binPrice?: number;
  binAvailable?: boolean;
  highBidderId?: number;
  status?: AuctionStatus;
}

const auction = (seed: AuctionSeed): AuctionState => ({
  id: seed.id,
  listingId: seed.listingId,
  startsAt: seed.startsAt,
  endsAt: seed.endsAt,
  startPrice: seed.startPrice,
  reservePrice: seed.reservePrice ?? null,
  reserveMet: seed.reserveMet ?? false,
  binPrice: seed.binPrice ?? null,
  binAvailable: seed.binAvailable ?? false,
  currentBid: seed.currentBid,
  // With no bids yet the first acceptable bid is the start price itself.
  minNextBid: seed.bidCount === 0 ? seed.startPrice : seed.currentBid + incrementFor(seed.currentBid),
  bidIncrement: incrementFor(seed.currentBid),
  bidCount: seed.bidCount,
  bidderCount: seed.bidderCount,
  highBidderId: seed.highBidderId ?? null,
  // Seeded bids predate the with-bid fulfilment choice - settle falls back
  // to pickup-first for these rows.
  highBidderFulfilment: null,
  extensionCount: 0,
  status: seed.status ?? AuctionStatus.Live
});

const handcraftedAuctions = (): AuctionState[] => [
  auction({
    id: 1001,
    listingId: 110,
    startsAt: daysAgo(5),
    endsAt: minutesFromNow(102),
    startPrice: 45,
    reservePrice: 120,
    reserveMet: true,
    binPrice: 325,
    binAvailable: true,
    currentBid: 146,
    bidCount: 12,
    bidderCount: 5
  }),
  auction({
    id: 1002,
    listingId: 111,
    startsAt: daysAgo(4),
    endsAt: minutesFromNow(190),
    startPrice: 30,
    reservePrice: 110,
    currentBid: 96,
    bidCount: 8,
    bidderCount: 4
  }),
  auction({
    id: 1003,
    listingId: 112,
    startsAt: daysAgo(3),
    endsAt: minutesFromNow(303),
    startPrice: 20,
    reservePrice: 55,
    reserveMet: true,
    currentBid: 61,
    bidCount: 5,
    bidderCount: 3
  }),
  auction({
    id: 1004,
    listingId: 113,
    startsAt: daysAgo(3),
    endsAt: hoursFromNow(28),
    startPrice: 8,
    currentBid: 22,
    bidCount: 2,
    bidderCount: 2,
    // The mock shopper (Jess) holds the high bid - "Winning" in My Bids.
    highBidderId: 1
  }),
  auction({
    id: 1005,
    listingId: 131,
    startsAt: daysAgo(6),
    endsAt: minutesFromNow(105),
    startPrice: 50,
    reservePrice: 130,
    reserveMet: true,
    currentBid: 146,
    bidCount: 12,
    bidderCount: 6
  }),
  auction({
    id: 1006,
    listingId: 132,
    startsAt: daysAgo(4),
    endsAt: minutesFromNow(195),
    startPrice: 35,
    reservePrice: 105,
    currentBid: 96,
    bidCount: 8,
    bidderCount: 5
  }),
  auction({
    id: 1007,
    listingId: 133,
    startsAt: daysAgo(5),
    endsAt: minutesFromNow(300),
    startPrice: 18,
    reservePrice: 55,
    reserveMet: true,
    currentBid: 61,
    bidCount: 5,
    bidderCount: 4
  }),
  auction({
    id: 1008,
    listingId: 134,
    startsAt: daysAgo(3),
    endsAt: hoursFromNow(29),
    startPrice: 8,
    currentBid: 22,
    bidCount: 2,
    bidderCount: 2
  }),
  auction({
    id: 1009,
    listingId: 149,
    startsAt: daysAgo(5),
    endsAt: minutesFromNow(45),
    startPrice: 40,
    reservePrice: 125,
    reserveMet: true,
    currentBid: 132,
    bidCount: 9,
    bidderCount: 5
  }),
  auction({
    id: 1010,
    listingId: 153,
    startsAt: daysAgo(4),
    endsAt: hoursFromNow(9),
    startPrice: 30,
    reservePrice: 95,
    currentBid: 84,
    bidCount: 6,
    bidderCount: 4
  }),
  auction({
    id: 1011,
    listingId: 158,
    startsAt: daysAgo(3),
    endsAt: hoursFromNow(52),
    startPrice: 12,
    currentBid: 41,
    bidCount: 4,
    bidderCount: 3
  }),
  auction({
    id: 1012,
    listingId: 163,
    startsAt: daysAgo(5),
    endsAt: daysFromNow(1),
    startPrice: 60,
    reservePrice: 150,
    reserveMet: true,
    currentBid: 156,
    bidCount: 11,
    bidderCount: 7
  }),
  auction({
    id: 1013,
    listingId: 168,
    startsAt: daysAgo(6),
    endsAt: daysFromNow(2),
    startPrice: 55,
    reservePrice: 160,
    reserveMet: true,
    currentBid: 178,
    bidCount: 14,
    bidderCount: 8
  }),
  auction({
    id: 1014,
    listingId: 172,
    startsAt: daysAgo(4),
    endsAt: daysFromNow(3),
    startPrice: 20,
    reservePrice: 80,
    currentBid: 66,
    bidCount: 7,
    bidderCount: 4
  }),
  auction({
    id: 1015,
    listingId: 176,
    startsAt: daysAgo(3),
    endsAt: daysFromNow(4),
    startPrice: 10,
    binPrice: 60,
    binAvailable: true,
    currentBid: 34,
    bidCount: 3,
    bidderCount: 3
  }),
  auction({
    id: 1016,
    listingId: 177,
    startsAt: daysAgo(6),
    endsAt: daysFromNow(6),
    startPrice: 65,
    reservePrice: 175,
    reserveMet: true,
    binPrice: 350,
    binAvailable: true,
    currentBid: 190,
    bidCount: 16,
    bidderCount: 9
  }),
  // Settled: won by the mock shopper (Jess) - drives the PDP won state and
  // the "Won" row in My Bids (AUC-07).
  auction({
    id: 1017,
    listingId: 181,
    startsAt: daysAgo(8),
    endsAt: hoursAgo(26),
    startPrice: 150,
    reservePrice: 350,
    reserveMet: true,
    currentBid: 410,
    bidCount: 18,
    bidderCount: 7,
    highBidderId: 1,
    status: AuctionStatus.Closed
  }),
  // Settled: Jess's proxy was outbid by an anonymous bidder - "Lost" in My Bids.
  auction({
    id: 1018,
    listingId: 182,
    startsAt: daysAgo(9),
    endsAt: daysAgo(2),
    startPrice: 25,
    currentBid: 132,
    bidCount: 11,
    bidderCount: 5,
    status: AuctionStatus.Closed
  }),
  // Scheduled: goes live in 6 hours (AUC-01) - excluded from search, PDP
  // shows the starts-in countdown with bidding disabled.
  auction({
    id: 1019,
    listingId: 183,
    startsAt: hoursFromNow(6),
    endsAt: hoursFromNow(78),
    startPrice: 60,
    reservePrice: 150,
    currentBid: 60,
    bidCount: 0,
    bidderCount: 0,
    status: AuctionStatus.Scheduled
  }),
  // Rows 1101-1105 mirror the New Mexico showcase auctions (listings 500-504),
  // one for each Goodwill NM store the generator left without an auction.
  auction({
    id: 1101,
    listingId: 500,
    startsAt: daysAgo(4),
    endsAt: hoursFromNow(4),
    startPrice: 75,
    reservePrice: 180,
    reserveMet: true,
    binPrice: 420,
    binAvailable: true,
    currentBid: 210,
    bidCount: 14,
    bidderCount: 6
  }),
  auction({
    id: 1102,
    listingId: 501,
    startsAt: daysAgo(3),
    endsAt: hoursFromNow(8),
    startPrice: 45,
    reservePrice: 160,
    currentBid: 128,
    bidCount: 9,
    bidderCount: 4
  }),
  auction({
    id: 1103,
    listingId: 502,
    startsAt: daysAgo(2),
    endsAt: hoursFromNow(30),
    startPrice: 30,
    currentBid: 74,
    bidCount: 7,
    bidderCount: 3
  }),
  auction({
    id: 1104,
    listingId: 503,
    startsAt: daysAgo(3),
    endsAt: hoursFromNow(19),
    startPrice: 40,
    reservePrice: 90,
    reserveMet: true,
    currentBid: 96,
    bidCount: 11,
    bidderCount: 5
  }),
  auction({
    id: 1105,
    listingId: 504,
    startsAt: daysAgo(2),
    endsAt: hoursFromNow(43),
    startPrice: 25,
    reservePrice: 85,
    currentBid: 58,
    bidCount: 6,
    bidderCount: 3
  }),
  // Rows 1106-1107 mirror the store-coverage auctions (listings 540-541) that
  // give the two Habitat ReStores the generator skipped a live auction.
  auction({
    id: 1106,
    listingId: 540,
    startsAt: daysAgo(4),
    endsAt: hoursFromNow(26),
    startPrice: 60,
    reservePrice: 150,
    reserveMet: true,
    currentBid: 185,
    bidCount: 8,
    bidderCount: 4
  }),
  auction({
    id: 1107,
    listingId: 541,
    startsAt: daysAgo(2),
    endsAt: hoursFromNow(34),
    startPrice: 50,
    reservePrice: 175,
    currentBid: 120,
    bidCount: 6,
    bidderCount: 3
  })
];

/**
 * One auction row per generated auction listing (ids 2001+), derived from the
 * same deterministic specs as `listing.seed.ts` so currentBid, increments and
 * reserves always agree with the catalog rows.
 */
const generatedAuctions = (): AuctionState[] => {
  let id = 2001;
  return generatedListingSpecs().flatMap(spec => {
    const generated = spec.auction;
    if (generated === null) return [];
    return [
      auction({
        id: id++,
        listingId: spec.id,
        startsAt: daysAgo(generated.startedDaysAgo),
        endsAt: minutesFromNow(generated.endsInMinutes),
        startPrice: generated.startPrice,
        reservePrice: generated.reservePrice ?? undefined,
        reserveMet: generated.reserveMet,
        binPrice: generated.buyNowPrice ?? undefined,
        binAvailable: generated.buyNowPrice !== null,
        currentBid: generated.currentBid,
        bidCount: generated.bidCount,
        bidderCount: generated.bidderCount
      })
    ];
  });
};

export const seedAuctions = (): AuctionState[] => [...handcraftedAuctions(), ...generatedAuctions()];

/**
 * Server-private standing/participation proxy rows (HLD §9.2). One row per
 * (auction, shopper) - the auction engine reads the highest row as the
 * standing proxy and My Bids derives participation from the shopper's rows.
 */
export interface AuctionProxyEntry {
  id: number;
  auctionId: number;
  /** Null for seeded anonymous bidders. */
  shopperId: number | null;
  maxBid: number;
}

export const seedAuctionProxies = (): AuctionProxyEntry[] => [
  // Jess holds the high bid on the Rossignol skis auction (1004).
  { id: 1, auctionId: 1004, shopperId: 1, maxBid: 40 },
  // Jess was outbid on the waterfall dresser auction (1010).
  { id: 2, auctionId: 1010, shopperId: 1, maxBid: 78 },
  // Jess won the Aeron chair (1017) at 410 with a 425 ceiling.
  { id: 3, auctionId: 1017, shopperId: 1, maxBid: 425 },
  // Jess lost the dive watch (1018) - an anonymous bidder went higher.
  { id: 4, auctionId: 1018, shopperId: 1, maxBid: 120 }
];

/** Settled results for the seeded closed auctions (HLD §17 `AuctionResult`). */
export const seedAuctionResults = (): AuctionResultState[] => [
  {
    id: 1017,
    auctionId: 1017,
    listingId: 181,
    winnerId: 1,
    winningAmount: 410,
    outcome: AuctionOutcome.Won,
    settledAt: hoursAgo(26),
    orderRef: 'BF-10455'
  },
  {
    id: 1018,
    auctionId: 1018,
    listingId: 182,
    winnerId: null,
    winningAmount: 132,
    outcome: AuctionOutcome.Won,
    settledAt: daysAgo(2),
    orderRef: null
  }
];

export const seedBids = (): BidState[] => {
  let nextId = 5001;
  const bid = (
    auctionId: number,
    bidderHandle: string,
    amount: number,
    placedAt: Date,
    isHighBid = false
  ): BidState => ({
    id: nextId++,
    auctionId,
    bidderHandle,
    amount,
    placedAt,
    status: isHighBid ? BidStatus.Active : BidStatus.Outbid,
    isHighBid
  });
  const rows: BidState[] = [
    // Auction 1001 - full 12-bid history matching the mockup PDP (ids 5001-5012)
    bid(1001, 'd***2', 146, minutesAgo(4), true),
    bid(1001, 'j***8', 141, minutesAgo(11)),
    bid(1001, 'd***2', 136, minutesAgo(26)),
    bid(1001, 'm***5', 120, hoursAgo(1)),
    bid(1001, 't***1', 95, hoursAgo(3)),
    bid(1001, 'k***9', 90, hoursAgo(4)),
    bid(1001, 'm***5', 84, hoursAgo(5)),
    bid(1001, 't***1', 78, hoursAgo(5)),
    bid(1001, 'k***9', 72, hoursAgo(6)),
    bid(1001, 'm***5', 65, hoursAgo(7)),
    bid(1001, 't***1', 58, hoursAgo(7)),
    bid(1001, 'k***9', 50, hoursAgo(8)),
    // Remaining auctions - 2-4 rows each; top bid matches currentBid
    bid(1002, 'r***4', 96, minutesAgo(22), true),
    bid(1002, 'a***7', 91, hoursAgo(1)),
    bid(1002, 'r***4', 80, hoursAgo(2)),
    bid(1002, 'g***3', 62, hoursAgo(5)),
    bid(1003, 'p***6', 61, minutesAgo(40), true),
    bid(1003, 'w***2', 58, hoursAgo(2)),
    bid(1003, 'p***6', 45, hoursAgo(6)),
    bid(1004, 'j***1', 22, hoursAgo(2), true),
    bid(1004, 'n***8', 15, hoursAgo(9)),
    bid(1005, 'c***3', 146, minutesAgo(8), true),
    bid(1005, 'f***7', 141, minutesAgo(30)),
    bid(1005, 'c***3', 128, hoursAgo(2)),
    bid(1005, 'l***2', 110, hoursAgo(4)),
    bid(1006, 's***9', 96, minutesAgo(50), true),
    bid(1006, 'd***4', 91, hoursAgo(2)),
    bid(1006, 'e***6', 75, hoursAgo(6)),
    bid(1007, 'h***5', 61, hoursAgo(1), true),
    bid(1007, 'v***3', 58, hoursAgo(3)),
    bid(1007, 'h***5', 40, hoursAgo(8)),
    bid(1008, 'y***7', 22, hoursAgo(3), true),
    bid(1008, 'q***2', 18, hoursAgo(10)),
    bid(1009, 'z***4', 132, minutesAgo(35), true),
    bid(1009, 'o***8', 127, hoursAgo(1)),
    bid(1009, 'z***4', 105, hoursAgo(3)),
    bid(1010, 'u***3', 84, hoursAgo(1), true),
    bid(1010, 'i***9', 81, hoursAgo(4)),
    bid(1010, 'j***1', 78, hoursAgo(5)),
    bid(1010, 'u***3', 60, hoursAgo(9)),
    bid(1011, 'e***2', 41, hoursAgo(2), true),
    bid(1011, 'x***6', 38, hoursAgo(7)),
    bid(1012, 'g***8', 156, minutesAgo(15), true),
    bid(1012, 'w***5', 151, hoursAgo(1)),
    bid(1012, 'g***8', 140, hoursAgo(2)),
    bid(1012, 'j***3', 120, hoursAgo(5)),
    bid(1013, 'm***7', 178, hoursAgo(1), true),
    bid(1013, 'k***2', 173, hoursAgo(3)),
    bid(1013, 'm***7', 150, hoursAgo(6)),
    bid(1013, 'b***9', 130, hoursAgo(12)),
    bid(1014, 't***4', 66, hoursAgo(2), true),
    bid(1014, 'r***1', 63, hoursAgo(5)),
    bid(1014, 'c***8', 50, hoursAgo(11)),
    bid(1015, 'n***5', 34, hoursAgo(4), true),
    bid(1015, 'f***2', 31, hoursAgo(10)),
    bid(1016, 'a***9', 190, hoursAgo(1), true),
    bid(1016, 'd***6', 185, hoursAgo(2)),
    bid(1016, 'a***9', 170, hoursAgo(5)),
    bid(1016, 's***3', 150, hoursAgo(9)),
    // Auction 1017 - closed, won by Jess (j***1) at 410
    bid(1017, 'j***1', 410, hoursAgo(26), true),
    bid(1017, 'r***6', 400, hoursAgo(27)),
    bid(1017, 'j***1', 385, hoursAgo(29)),
    bid(1017, 'v***8', 360, hoursAgo(31)),
    bid(1017, 'r***6', 320, hoursAgo(38)),
    // Auction 1018 - closed, Jess (j***1) outbid by an anonymous winner
    bid(1018, 'm***4', 132, hoursAgo(49), true),
    bid(1018, 'j***1', 120, hoursAgo(50)),
    bid(1018, 'm***4', 111, hoursAgo(52)),
    bid(1018, 'g***7', 95, hoursAgo(60))
  ];
  // Generated auctions (ids 2001+): 2-3 anonymized rows apiece through the
  // same helper - the top row always matches currentBid (AUC-08 consistency).
  const handleAlphabet = 'abcdefghijkmnprstvwyz';
  const handle = (n: number): string => `${handleAlphabet[n % handleAlphabet.length]}***${(n * 3) % 10}`;
  let generatedAuctionId = 2001;
  let sequence = 0;
  for (const spec of generatedListingSpecs()) {
    if (spec.auction === null) continue;
    const generated = spec.auction;
    rows.push(
      bid(generatedAuctionId, handle(sequence), generated.currentBid, minutesAgo(10 + ((sequence * 17) % 110)), true)
    );
    rows.push(
      bid(
        generatedAuctionId,
        handle(sequence + 7),
        generated.currentBid - generated.bidStep,
        hoursAgo(2 + (sequence % 8))
      )
    );
    if (generated.bidCount > 4) {
      rows.push(bid(generatedAuctionId, handle(sequence + 3), generated.startPrice, hoursAgo(12 + (sequence % 24))));
    }
    generatedAuctionId++;
    sequence++;
  }
  return rows;
};
